/**
 * Zwergenwald - Hinweise im Warenkorb (blocks/zw-warenkorb-hinweise.liquid, O-93)
 *
 * <zw-warenkorb-hinweise> zeigt zwei Zeilen:
 *   1. Lieferdatum "Voraussichtlich bei dir: [von]–[bis]" - dieselbe Rechnung wie die Kaufbox
 *      (versandTexte aus assets/zw-lieferinfo.js, per dynamischem Import mit Versions-URL).
 *   2. Reservierungs-Timer - Kanzlei-Vorbehalt O-93 (Liste Nr. 2). Shopify reserviert im Warenkorb
 *      nichts; der Timer ist "zum Gucken" (W-4). Die Liquid-Seite gibt ihn nur aus, wenn das Theme
 *      nicht veroeffentlicht ist, im Theme-Editor oder nach gesetzter Kanzlei-Freigabe. Startzeit
 *      pro Browser-Sitzung (sessionStorage); bei 0 verschwindet die Zeile, sie beginnt nicht neu.
 *
 * B-Ware (Auftrag B-Ware Bettgitter, steuernde Sitzung 07.10.2026): Liegt eine Position mit SKU-Praefix
 * "BWARE-" im Warenkorb, ist die Datumszeile verborgen (sie rechnet mit Neuware-Werten) und der B-Ware-Satz
 * (Einstellung bware_text) sichtbar; ohne B-Ware umgekehrt. Die Summenzeile von Horizon
 * (snippets/cart-summary.liquid -> tax-info -> locales/de.json, "Nach Österreich: 4,99 € Versand.") wird
 * nur bei B-Ware angepasst: nur B-Ware -> "Nach Österreich: 10,00 € Versand." (Wortlaut der B-Ware-Preisangabe),
 * B-Ware und andere Ware -> der Satz entfaellt, der B-Ware-Satz nennt dann beide Betraege. Ohne B-Ware
 * bleibt der Text, wie Horizon ihn rendert. Quelle des Zustands: beim Seitenaufruf die Vorbelegung aus Liquid
 * (data-bware / data-neuware), danach /cart.js nach jeder Warenkorb-Aenderung (Ereignis
 * "shopify:cart:lines-update") und bei Rueckkehr aus dem Browser-Cache (pageshow). In React-Begriffen: ein
 * kleiner globaler Store, den Effekte nach jedem Render (MutationObserver) auf das DOM anwenden.
 * Kein Eingriff in Horizon-Dateien.
 *
 * B-Ware ohne Vereinbarung (Code-Pruefung Teil 3, M6, 09.10.2026): Die Zustimmung nach § 476 Abs. 1 Satz 2 BGB haengt
 * an der Bestellzeilen-Eigenschaft "Vereinbarte Abweichung" (Wert = fester Text, rechtstexte/15). Sie entsteht auf der
 * B-Ware-Seite ueber den Direktlink. Liegt eine BWARE-Position OHNE diese Eigenschaft (oder mit abweichendem Wert) im
 * Warenkorb - etwa nach /cart/add?id=... -, ist "Zur Kasse" gesperrt (auch die Express-Knoepfe), bis das Haekchen im
 * Warenkorb gesetzt ist. Das Haekchen setzt die Eigenschaft per POST /cart/change.js (shopify.dev/docs/api/ajax/
 * reference/cart: "properties" einer Zeile aendern, ersetzt alle Eigenschaften der Zeile; die Zeile wird ueber ihre
 * 1-basierte Position gewaehlt). Seit 17.10.2026 waehlt die SKU das feste Profil aus zw-bware-vereinbarung.js:
 * BWARE-NS-110-... ist Nestschaukel, sonst BWARE-... Bettgitter. Beide koennen getrennt bestaetigt werden. Ein
 * veraenderter Text aus DOM oder Zeileneigenschaft gilt nie als Vorgabe. Im normalen Ablauf tragen alle B-Ware-Positionen die Eigenschaft - dann bleibt
 * das Feld verborgen und nichts aendert sich. Grenze: /checkout direkt aufzurufen kann ein Theme nicht verhindern.
 *
 * Im Drawer setzt das Modul die Vorlage als LETZTES Element von .cart-drawer__items ein (unter den
 * Artikeln, ueber der Summenbox). Grund wie bei assets/zw-gewaehrleistung-drawer.js: Horizons
 * Abgleich (morph) entfernt einen fremden Knoten am Ende nur, statt Geschwister umzuhaengen; der
 * MutationObserver setzt ihn sofort wieder ein. Die EU-Mitteilung sitzt in .cart-totals - die
 * beiden kommen sich nicht in die Quere. Abhaengig von #cart-drawer, .cart-drawer__items und
 * .cart-totals__tax-note (Horizon 4.1.4) - beim Horizon-Update pruefen.
 */
import { vereinbarungFuerPosition, vereinbarungFuerProfil } from './zw-bware-vereinbarung.js';

const SPEICHER_START = 'zw-reservierung-start';

function speicherLesen() {
  try {
    return window.sessionStorage.getItem(SPEICHER_START);
  } catch {
    return null;
  }
}

function speicherSchreiben(wert) {
  try {
    window.sessionStorage.setItem(SPEICHER_START, wert);
  } catch {
    /* privates Fenster o. Ae.: Timer laeuft dann nur bis zum Neuladen */
  }
}

const zweistellig = (zahl) => String(zahl).padStart(2, '0');

// ---------------------------------------------------------------------------
// B-Ware: Zustand des Warenkorbs und seine Anzeige
// ---------------------------------------------------------------------------

const BWARE_PRAEFIX = 'BWARE-';
const AT_NEUWARE = 'Nach Österreich: 4,99 € Versand.';
const AT_BWARE = 'Nach Österreich: 10,00 € Versand.';

/** @typedef {{ eigenschaft: string, abweichung: string }} Vereinbarung */
/** @typedef {{ bware: boolean, neuware: boolean, bwareOffen: boolean, bwareProfileOffen: string[] }} WarenkorbArt */

/** @param {{ sku?: string | null } | null | undefined} position */
export function istBware(position) {
  return String(position?.sku ?? '')
    .toUpperCase()
    .startsWith(BWARE_PRAEFIX);
}

/**
 * Traegt die Position die Vereinbarung? Nur wenn die Eigenschaft GENAU den festen Text hat.
 * @param {{ sku?: string | null, properties?: Record<string, unknown> | null } | null | undefined} position
 */
export function istVereinbart(position) {
  const vereinbarung = vereinbarungFuerPosition(position);
  if (!vereinbarung) return false;
  const wert = position?.properties?.[vereinbarung.eigenschaft];
  return typeof wert === 'string' && wert === vereinbarung.abweichung;
}

/**
 * @param {Array<{ sku?: string | null, properties?: Record<string, unknown> | null }> | null | undefined} positionen - items aus /cart.js
 * @returns {WarenkorbArt}
 */
export function warenkorbAuswerten(positionen) {
  const liste = Array.isArray(positionen) ? positionen : [];
  const bwareProfileOffen = [...new Set(liste
    .filter((p) => istBware(p) && !istVereinbart(p))
    .map((p) => vereinbarungFuerPosition(p).profil))];
  return {
    bware: liste.some(istBware),
    neuware: liste.some((p) => !istBware(p)),
    bwareOffen: bwareProfileOffen.length > 0,
    bwareProfileOffen,
  };
}

/**
 * Text der Summenzeile fuer den gegebenen Warenkorb. Ohne B-Ware (oder unbekannt) unveraendert.
 * @param {string} original - Text, wie Horizon ihn rendert
 * @param {WarenkorbArt | null} art
 */
export function summenText(original, art) {
  if (!art?.bware || !original.includes(AT_NEUWARE)) return original;
  if (art.neuware) return original.replace(`${AT_NEUWARE} `, '').replace(AT_NEUWARE, '');
  return original.replace(AT_NEUWARE, AT_BWARE);
}

/** @type {WarenkorbArt | null} */
let warenkorbArt = null;
let abrufNummer = 0;
let nachholenLaeuft = false;

/** Vorbelegung aus Liquid (Stand beim Seitenaufruf) - aus der ersten Vorlage, die sie traegt. */
function artAusVorlage() {
  for (const vorlage of document.querySelectorAll('template[data-zw-warenkorb-drawer], template[data-zw-kasse-seite]')) {
    if (!(vorlage instanceof HTMLTemplateElement)) continue;
    const quelle = vorlage.content.querySelector('zw-warenkorb-hinweise');
    if (quelle instanceof HTMLElement && quelle.dataset.bware) {
      return {
        bware: quelle.dataset.bware === 'true',
        neuware: quelle.dataset.neuware === 'true',
        bwareOffen: quelle.dataset.bwareOffen === 'true',
        bwareProfileOffen: ['bettgitter', 'nestschaukel'].filter((profil) =>
          quelle.dataset[profil === 'bettgitter' ? 'bwareBettgitterOffen' : 'bwareNestschaukelOffen'] === 'true'),
      };
    }
  }
  return null;
}

/** @param {Node} knoten @returns {Generator<Text>} */
function* textKnoten(knoten) {
  for (const kind of Array.from(knoten.childNodes)) {
    if (kind.nodeType === 3) yield /** @type {Text} */ (kind);
    else if (kind.nodeType === 1) yield* textKnoten(kind);
  }
}

/** Originaltext je Textknoten, den dieses Modul geaendert hat (fuer die Rueckkehr ohne B-Ware). */
const satzVorher = new WeakMap();

function summenzeilenAnwenden() {
  for (const notiz of document.querySelectorAll('.cart-totals__tax-note')) {
    for (const knoten of textKnoten(notiz)) {
      const bekannt = satzVorher.get(knoten);
      // Hat Horizon den Text inzwischen neu gesetzt, gilt der neue Text als Original.
      const original = bekannt && knoten.data === bekannt.gesetzt ? bekannt.original : knoten.data;
      const soll = summenText(original, warenkorbArt);
      if (knoten.data !== soll) knoten.data = soll;
      if (soll === original) satzVorher.delete(knoten);
      else satzVorher.set(knoten, { original, gesetzt: soll });
    }
  }
}

/** @param {ParentNode} wurzel */
function zeilenAnwenden(wurzel) {
  if (!warenkorbArt) return;
  for (const zeile of wurzel.querySelectorAll('[data-zw-liefer-zeile]')) {
    if (zeile instanceof HTMLElement && zeile.hidden !== warenkorbArt.bware) zeile.hidden = warenkorbArt.bware;
  }
  for (const zeile of wurzel.querySelectorAll('[data-zw-bware-zeile]')) {
    if (zeile instanceof HTMLElement && zeile.hidden !== !warenkorbArt.bware) zeile.hidden = !warenkorbArt.bware;
  }
  for (const feld of wurzel.querySelectorAll('[data-zw-bware-pflicht]')) {
    const offen = feld instanceof HTMLElement && warenkorbArt.bwareProfileOffen.includes(feld.dataset.profil);
    if (feld instanceof HTMLElement && feld.hidden !== !offen) feld.hidden = !offen;
    const haken = feld.querySelector('[data-zw-bware-haken]');
    if (haken instanceof HTMLInputElement && haken.disabled !== nachholenLaeuft) haken.disabled = nachholenLaeuft;
  }
}

/** Ist die Kasse gesperrt? Solange eine B-Ware-Position ohne Vereinbarung da ist oder die Eigenschaft gerade gesetzt wird. */
function kasseGesperrt() {
  return Boolean(warenkorbArt?.bwareOffen) || nachholenLaeuft;
}

/**
 * Sperrt "Zur Kasse" (button[name=checkout]) und die Express-Knoepfe oder gibt sie wieder frei. Gibt nur frei, was dieses
 * Modul selbst gesperrt hat (data-zw-gesperrt); ein von Horizon gesperrter Knopf (leerer Warenkorb) bleibt gesperrt.
 */
function kasseAnwenden() {
  const sperren = kasseGesperrt();
  for (const knopf of document.querySelectorAll('button[name="checkout"]')) {
    if (!(knopf instanceof HTMLButtonElement)) continue;
    if (sperren) {
      if (!knopf.disabled) knopf.disabled = true;
      knopf.dataset.zwGesperrt = 'true';
    } else if (knopf.dataset.zwGesperrt) {
      knopf.disabled = false;
      delete knopf.dataset.zwGesperrt;
    }
  }
  for (const feld of document.querySelectorAll('.additional-checkout-buttons')) {
    if (!(feld instanceof HTMLElement)) continue;
    if (sperren) {
      if (feld.style.display !== 'none') feld.style.display = 'none';
      feld.dataset.zwGesperrt = 'true';
    } else if (feld.dataset.zwGesperrt) {
      feld.style.removeProperty('display');
      delete feld.dataset.zwGesperrt;
    }
  }
}

function artAnwenden() {
  zeilenAnwenden(document);
  summenzeilenAnwenden();
  kasseAnwenden();
}

/** Liest den Warenkorb neu; nur die Antwort des letzten Abrufs zaehlt. */
async function artLaden() {
  const nummer = ++abrufNummer;
  try {
    const wurzel = window.Shopify?.routes?.root ?? '/';
    const antwort = await fetch(`${wurzel}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!antwort.ok) return;
    const warenkorb = await antwort.json();
    if (nummer !== abrufNummer) return;
    warenkorbArt = warenkorbAuswerten(warenkorb?.items);
    artAnwenden();
  } catch (fehler) {
    console.warn('[zw-warenkorb] Warenkorb nicht gelesen:', fehler);
  }
}

/** @param {string} text */
function meldungSetzen(text) {
  for (const meldung of document.querySelectorAll('[data-zw-bware-meldung]')) {
    if (!(meldung instanceof HTMLElement)) continue;
    meldung.textContent = text;
    meldung.hidden = !text;
  }
}

/**
 * Setzt die Eigenschaft an allen B-Ware-Positionen ohne Vereinbarung (eine Zeile je Aufruf von change.js, danach
 * /cart.js neu lesen, weil sich Zeilenpositionen aendern koennen, wenn Zeilen zusammenfallen).
 * @param {string} profil
 */
export async function vereinbarungNachholen(profil) {
  const soll = vereinbarungFuerProfil(profil);
  if (!soll) throw new Error('Unbekannte B-Ware-Vereinbarung');
  const wurzel = window.Shopify?.routes?.root ?? '/';
  for (let runde = 0; runde < 10; runde += 1) {
    const antwort = await fetch(`${wurzel}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!antwort.ok) throw new Error(`cart.js ${antwort.status}`);
    const positionen = (await antwort.json())?.items ?? [];
    const index = positionen.findIndex((p) => vereinbarungFuerPosition(p)?.profil === profil && !istVereinbart(p));
    if (index === -1) return;
    const aendern = await fetch(`${wurzel}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: index + 1,
        properties: { ...(positionen[index].properties ?? {}), [soll.eigenschaft]: soll.abweichung },
      }),
    });
    if (!aendern.ok) throw new Error(`change.js ${aendern.status}`);
  }
  throw new Error('Zu viele B-Ware-Positionen');
}

/** @param {HTMLInputElement} haken */
async function hakenGeaendert(haken) {
  const profil = haken.closest('[data-zw-bware-pflicht]')?.dataset.profil;
  if (!haken.checked || !vereinbarungFuerProfil(profil) || nachholenLaeuft) return;
  nachholenLaeuft = true;
  meldungSetzen('');
  artAnwenden();
  try {
    await vereinbarungNachholen(profil);
  } catch (fehler) {
    console.warn('[zw-warenkorb] Vereinbarung nicht gesetzt:', fehler);
    haken.checked = false;
    meldungSetzen('Das hat nicht geklappt. Bitte versuche es noch einmal.');
  }
  nachholenLaeuft = false;
  await artLaden();
  // Auf der Warenkorbseite zeigt eine frisch geladene Seite die Zeile samt Eigenschaft; die Schublade bleibt offen.
  if (!warenkorbArt?.bwareOffen && document.querySelector('.cart-page')) window.location.reload();
}

// ---------------------------------------------------------------------------
// Element <zw-warenkorb-hinweise>
// ---------------------------------------------------------------------------

class ZwWarenkorbHinweise extends HTMLElement {
  /** @type {number | undefined} */
  #takt;

  connectedCallback() {
    zeilenAnwenden(this);
    this.#lieferdatum();
    this.#timerStarten();
  }

  disconnectedCallback() {
    if (this.#takt) window.clearInterval(this.#takt);
    this.#takt = undefined;
  }

  async #lieferdatum() {
    const zeile = this.querySelector('[data-zw-spanne]');
    const quelle = this.dataset.lieferinfoSrc;
    if (!(zeile instanceof HTMLElement) || !quelle) return;
    try {
      const { versandTexte } = await import(quelle);
      const konfig = {
        versandtage: new Set(
          String(this.dataset.versandtage ?? '')
            .split(',')
            .map((wert) => Number.parseInt(wert, 10))
            .filter((wert) => wert >= 0 && wert <= 6)
        ),
        schliesstage: new Set(
          String(this.dataset.schliesstage ?? '')
            .split(/[\s,;]+/)
            .map((wert) => wert.trim())
            .filter((wert) => /^\d{4}-\d{2}-\d{2}$/.test(wert))
        ),
        annahmeschluss: Number.parseInt(this.dataset.annahmeschluss ?? '660', 10) || 0,
      };
      const ergebnis = versandTexte(
        new Date(),
        konfig,
        { heute: '', spaeter: '', spanne: this.dataset.textSpanne ?? '' },
        {
          countdown: false,
          spanne: true,
          laufzeitMin: Number.parseInt(this.dataset.laufzeitMin ?? '2', 10),
          laufzeitMax: Number.parseInt(this.dataset.laufzeitMax ?? '3', 10),
        }
      );
      if (ergebnis.spanne) {
        zeile.textContent = ergebnis.spanne;
        zeile.hidden = false;
      }
    } catch (fehler) {
      console.warn('[zw-warenkorb] Lieferdatum nicht berechnet:', fehler);
    }
  }

  #timerStarten() {
    const zeile = this.querySelector('[data-zw-timer]');
    const anzeige = zeile?.querySelector('[data-zw-timer-zeit]');
    const minuten = Number.parseInt(this.dataset.timerMinuten ?? '0', 10);
    if (!(zeile instanceof HTMLElement) || !(anzeige instanceof HTMLElement) || !(minuten > 0)) return;

    let start = Number.parseInt(speicherLesen() ?? '', 10);
    if (!Number.isFinite(start)) {
      start = Date.now();
      speicherSchreiben(String(start));
    }
    const ende = start + minuten * 60 * 1000;

    const aktualisieren = () => {
      const rest = Math.max(0, Math.round((ende - Date.now()) / 1000));
      if (rest <= 0) {
        zeile.hidden = true;
        if (this.#takt) window.clearInterval(this.#takt);
        this.#takt = undefined;
        return;
      }
      anzeige.textContent = `${Math.floor(rest / 60)}:${zweistellig(rest % 60)}`;
      zeile.hidden = false;
    };

    aktualisieren();
    this.#takt = window.setInterval(aktualisieren, 1000);
  }
}

if (!customElements.get('zw-warenkorb-hinweise')) {
  customElements.define('zw-warenkorb-hinweise', ZwWarenkorbHinweise);
}

// Beobachtet auch Textaenderungen: Horizons Abgleich kann die Summenzeile neu setzen, ohne Knoten zu tauschen.
const BEOBACHTEN = { childList: true, subtree: true, characterData: true };

// Einsetzen in den Drawer (nur wenn die Vorlage aus dem Fussbereich da ist)
const vorlage = document.querySelector('template[data-zw-warenkorb-drawer]');
const drawer = document.getElementById('cart-drawer');

if (vorlage instanceof HTMLTemplateElement && drawer) {
  const einsetzen = () => {
    for (const liste of drawer.querySelectorAll('.cart-drawer__items')) {
      if (liste.lastElementChild?.localName === 'zw-warenkorb-hinweise') continue;
      liste.querySelector(':scope > zw-warenkorb-hinweise')?.remove();
      liste.append(vorlage.content.cloneNode(true));
    }
    artAnwenden();
  };

  einsetzen();
  new MutationObserver(einsetzen).observe(drawer, BEOBACHTEN);
}

// Warenkorbseite: Lieferdatum, Timer, Vertrauenszeilen und Zahlungssymbole gebuendelt ueber
// "Zur Kasse" (Pruefbericht Punkt 8, Schlusspruefung Runde 2 MUSS 3, O-60) - wie in der Schublade,
// am Handy also vor der Kaufentscheidung. Gleiches Verfahren wie die EU-Mitteilung in der
// Schublade: letztes Element von .cart-totals, weil Horizons Abgleich einen fremden Knoten am Ende
// nur entfernt; der MutationObserver setzt ihn wieder ein.
// Abhaengig von .cart-page und .cart-totals (Horizon 4.1.4) - beim Horizon-Update pruefen.
const kasse = document.querySelector('template[data-zw-kasse-seite]');
const seite = document.querySelector('.cart-page')?.closest('.shopify-section') ?? null;

if (kasse instanceof HTMLTemplateElement && seite) {
  const kasseEinsetzen = () => {
    for (const summe of seite.querySelectorAll('.cart-totals')) {
      if (summe.closest('#cart-drawer')) continue;
      if (summe.lastElementChild?.localName === 'zw-kasse-seite') continue;
      summe.querySelector(':scope > zw-kasse-seite')?.remove();
      summe.append(kasse.content.cloneNode(true));
    }
    artAnwenden();
  };

  kasseEinsetzen();
  new MutationObserver(kasseEinsetzen).observe(seite, BEOBACHTEN);
}

// B-Ware: Zustand vorbelegen, dann mit /cart.js abgleichen und nach jeder Aenderung neu lesen.
// Schublade und Seite laden dieselbe Datei; der Browser fuehrt ein Modul je Adresse nur einmal aus.
if (vorlage || kasse) {
  warenkorbArt = artAusVorlage();
  artAnwenden();
  artLaden();
  document.addEventListener('change', (ereignis) => {
    const ziel = ereignis.target;
    if (ziel instanceof HTMLInputElement && ziel.matches('[data-zw-bware-haken]')) hakenGeaendert(ziel);
  });
  // Rueckfall: gibt Horizon den Knopf nach einer Mengenaenderung wieder frei, sperrt ihn der Beobachter sofort erneut ...
  new MutationObserver(() => {
    if (kasseGesperrt()) kasseAnwenden();
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['disabled', 'style'] });
  // ... und ein Klick oder Absenden zur Kasse geht in dieser Zeit nirgendwohin.
  document.addEventListener(
    'click',
    (ereignis) => {
      if (!kasseGesperrt()) return;
      const ziel = ereignis.target instanceof Element ? ereignis.target.closest('[name="checkout"], .additional-checkout-buttons') : null;
      if (!ziel) return;
      ereignis.preventDefault();
      ereignis.stopImmediatePropagation();
    },
    true
  );
  document.addEventListener(
    'submit',
    (ereignis) => {
      if (!kasseGesperrt()) return;
      const absender = /** @type {SubmitEvent} */ (ereignis).submitter;
      if (absender instanceof HTMLElement && absender.getAttribute('name') === 'checkout') {
        ereignis.preventDefault();
        ereignis.stopImmediatePropagation();
      }
    },
    true
  );
  document.addEventListener('shopify:cart:lines-update', (ereignis) => {
    const versprechen = /** @type {{ promise?: Promise<unknown> }} */ (ereignis).promise;
    if (versprechen && typeof versprechen.then === 'function') versprechen.then(artLaden, () => {});
    else artLaden();
  });
  window.addEventListener('pageshow', (ereignis) => {
    if (/** @type {PageTransitionEvent} */ (ereignis).persisted) artLaden();
  });
}
